import { useState } from 'react';
import { Plus, Trash2, Shield, Info } from 'lucide-react';

interface AllowListConfigProps {
  allowList: string[];
  onChange: (allowList: string[]) => void;
}

const PRESET_SELECTORS = [
  { label: '代码块', value: 'pre, code' },
  { label: '输入框', value: 'input, textarea' },
  { label: '引用区域', value: 'blockquote' },
  { label: '链接', value: 'a' },
];

export function AllowListConfig({ allowList, onChange }: AllowListConfigProps) {
  const [newSelector, setNewSelector] = useState('');

  const addSelector = () => {
    if (newSelector.trim() && !allowList.includes(newSelector.trim())) {
      onChange([...allowList, newSelector.trim()]);
      setNewSelector('');
    }
  };

  const removeSelector = (selector: string) => {
    onChange(allowList.filter(s => s !== selector));
  };

  const addPreset = (value: string) => {
    if (!allowList.includes(value)) {
      onChange([...allowList, value]);
    }
  };

  return (
    <div className="bg-white rounded p-6 border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5 text-[#333333]" />
        <h3 className="text-lg font-semibold text-[#2c3e50]">白名单设置</h3>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 flex gap-2">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          白名单中的元素将不受保护限制，用户可以自由复制。支持 CSS 选择器语法。
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-[#2c3e50] mb-2">
          快速添加预设
        </label>
        <div className="flex flex-wrap gap-2">
          {PRESET_SELECTORS.map(preset => (
            <button
              key={preset.value}
              onClick={() => addPreset(preset.value)}
              disabled={allowList.includes(preset.value)}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 
                         disabled:bg-gray-50 disabled:text-gray-400
                         text-[#333333] rounded transition-colors"
            >
              + {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-[#2c3e50] mb-2">
          自定义选择器
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSelector}
            onChange={(e) => setNewSelector(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSelector()}
            placeholder="例如: .allow-copy, #share-section"
            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm
                       focus:outline-none focus:ring-2 focus:ring-[#333333]/20
                       text-[#2c3e50]"
          />
          <button
            onClick={addSelector}
            disabled={!newSelector.trim()}
            className="px-4 py-2 bg-[#333333] text-white rounded text-sm
                       hover:bg-gray-800 disabled:bg-gray-300
                       transition-colors flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            添加
          </button>
        </div>
      </div>

      {allowList.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-[#2c3e50] mb-2">
            已配置的白名单 ({allowList.length})
          </label>
          <div className="space-y-2">
            {allowList.map((selector, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-3 py-2 
                           bg-gray-50 border border-gray-200 rounded"
              >
                <code className="text-sm text-[#333333] font-mono">
                  {selector}
                </code>
                <button
                  onClick={() => removeSelector(selector)}
                  className="p-1 text-gray-400 hover:text-[#e74c3c] 
                             hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
